/**
 * billingUpgradeInterceptor — the GLOBAL backbone that makes every
 * entitlement-gated route (today: exam-start, tailored-practice; F13-PR4's
 * study-plan GET the moment it lands — see below) degrade to a warm upgrade
 * dialog instead of throwing a raw 402 at the student, with ZERO per-surface
 * wiring. Mirrors coppa/ageConsentInterceptor.js's shape exactly (installed
 * the SAME way, on the SAME shared axios instance, alongside the 401 and
 * age/consent interceptors — see App.js).
 *
 * ONE behavior: 402 `entitlement_required` (the caller is authenticated and
 * allowed, but the feature is premium and they hold no live entitlement) ->
 * raise the shared <UpgradeDialog> with the refusal's OWN message/next_steps/
 * meter numbers, then REJECT the original promise. UNLIKE the age
 * interceptor's 426 branch, this NEVER retries: buying premium is an
 * out-of-band redirect-to-Stripe-then-webhook flow, not something a
 * transparent retry of the ORIGINAL request could ever resolve inline — the
 * exact same reasoning ageConsentInterceptor.js already documents for its
 * OWN 403 consent_required branch (raise the overlay, reject, no retry: "the
 * unfreeze needs a guardian action that may take days"). Here the "guardian
 * action" is instead "a completed Stripe checkout", so the calling surface
 * still gets its rejection and can render its own short inline fallback
 * (mirrors ExamScoreReport's TailoredPracticeCta `consentRequired`/
 * `dobRequired` phases) while the dialog is the shared "upgrade" affordance
 * on top.
 *
 * GENERALIZES FOR FREE TO ANY FUTURE 402 SURFACE: this classifies purely by
 * status + machine code (billingApi.isEntitlementRequired), never by URL —
 * so F13-PR4's study-plan UI, once it exists, gets this SAME dialog with
 * ZERO additional interception code. The dialog's own two-variant rendering
 * (meter numbers vs. pure-premium) is driven entirely by whether the
 * payload carries `free_allowance`/`free_used` (billingApi.meterFromError),
 * which is exactly how the backend already distinguishes a metered refusal
 * (exam/tailored) from a pure-premium one (the study plan) — see
 * billing_entitlements.py `_upgrade_refusal`.
 *
 * BACKGROUND requests (`x-background-request`) are skipped — a prefetch must
 * never pop a dialog in the student's face (mirrors ageConsentInterceptor's
 * identical guard).
 */
import { showUpgrade } from './billingUpgradeController';
import { isEntitlementRequired, nextSteps, meterFromError, readErrorCode } from './billingApi';
import { readMessage } from '../api/httpError';

/** True if the request was tagged a background/prefetch call (skip the UI). */
function isBackground(config) {
  const h = config?.headers || {};
  return (
    h['x-background-request'] === 'true' || h['X-Background-Request'] === 'true'
  );
}

/**
 * Install the billing-upgrade response interceptor on `axiosInstance`.
 * Returns the interceptor id so the caller can eject it (App.js ejects on
 * unmount, mirroring the 401 + age/consent interceptors). `deps` lets tests
 * inject a fake `onShow` without monkey-patching the controller module.
 */
export function installBillingUpgradeInterceptor(axiosInstance, deps = {}) {
  const onShow = deps.showUpgrade || showUpgrade;

  return axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      const config = error?.config;

      if (!config || isBackground(config)) {
        return Promise.reject(error);
      }

      if (isEntitlementRequired(error)) {
        const { allowance, used } = meterFromError(error);
        onShow({
          code: readErrorCode(error),
          message: readMessage(error),
          nextSteps: nextSteps(error),
          freeAllowance: allowance,
          freeUsed: used,
        });
      }

      // Not ours, OR ours-but-never-retried: the calling surface's own
      // catch still sees the rejection either way.
      return Promise.reject(error);
    },
  );
}
